import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Layers3, Check, Trash2, ShieldCheck, TrendingUp, TrendingDown, ImageOff, Tag, Hash } from "lucide-react";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { useCurrency } from "../context/CurrencyContext";
import { cardTotalValue } from "../lib/conditions";

export default function CardTile({ card, index = 0, selectable = false, selected = false, onToggleSelect, onDelete, onRemoveMatch, onSaleStatus, viewOnly = false, onView, formatPrice }) {
  const currencyCtx = useCurrency();
  const fmt = formatPrice || currencyCtx?.format || ((v) => (v == null ? null : `$${v.toFixed(2)}`));
  const totalValue = cardTotalValue(card);
  const [webImageFailed, setWebImageFailed] = useState(false);
  const handleCheckboxClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleSelect?.(card.id);
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`Remove "${card.name}" from your binder?`)) {
      onDelete?.(card.id);
    }
  };

  const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);
  const scannedImage = card.captured_image_url ? resolveUrl(card.captured_image_url) : null;
  const matchImage = !webImageFailed && card.reference_image_url ? card.reference_image_url : null;
  // Default card photo is the collector's own scan; the AI web match is only shown as the main
  // image when the user explicitly picked it. Cards from before this rule keep the old behaviour.
  const wantScan = (card.display_image || (card.reference_image_url ? "match" : "scan")) === "scan";
  const primaryImage = wantScan ? (scannedImage || matchImage) : (matchImage || scannedImage);
  const hoverImage = wantScan ? matchImage : scannedImage;
  const showHover = hoverImage && hoverImage !== primaryImage;
  const hoverLabel = primaryImage === scannedImage ? "Web match" : viewOnly ? "Their scan" : "Your scan";

  // A dead web-match URL shouldn't leave a broken tile - fall back to the user's own scan.
  const handleWebImageError = (e) => {
    const src = e.target.src || "";
    if (src.includes("images.pricecharting.com")) {
      const m = src.match(/\/(\d+)\.jpg$/);
      if (m && m[1] !== "60") {
        pcImageFallback(e);
        return;
      }
    }
    if (card.reference_image_url && scannedImage && !webImageFailed) setWebImageFailed(true);
  };
  const isEstimate = card.price_source === "estimate_similar" || card.price_source === "ai_estimate";
  const trend = card.is_graded ? card.sold_graded_trend : card.sold_raw_trend;
  const showTrend = trend != null && Math.abs(trend) >= 2;
  const SOURCE_LABELS = {
    sold_comps: "eBay Sold Listings",
    market_data_scrape: "Market Data",
    estimate_similar: "Similar-cards estimate",
    ai_estimate: "AI market estimate",
    manual: "Manual entry",
  };
  const priceTitle = `Estimated market value — based on recent completed sales.${SOURCE_LABELS[card.price_source] ? ` Source: ${SOURCE_LABELS[card.price_source]}` : ""}`;
  const addedStr = card.created_at
    ? new Date(card.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : null;
  const confidenceDot = { high: "bg-emerald-400", medium: "bg-amber-400", low: "bg-red-400" }[card.id_confidence];
  const saleStatus = card.sale_status; // under_offer | sold_stc | null
  const salePrice = saleStatus === "under_offer" ? card.offer_price : saleStatus === "sold_stc" ? card.sold_price : null;
  const saleGray = saleStatus ? "grayscale-[0.45]" : "";
  // Lift the corner action buttons above the status banner so it stays readable.
  const actionBottom = saleStatus ? (salePrice != null ? "bottom-12" : "bottom-8") : "bottom-2";

  const Wrapper = viewOnly ? "div" : Link;
  const wrapperProps = viewOnly
    ? { onClick: () => onView?.(card), role: "button", tabIndex: 0 }
    : {
        to: `/card/${card.id}`,
        onClick: (e) => {
          if (selectable) {
            e.preventDefault();
            onToggleSelect?.(card.id);
          }
        },
      };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`card-tile-${card.id}`}
      className={`group relative flex flex-col bg-[#111113] border rounded-xl overflow-hidden transition-transform duration-300 animate-fade-up hover:-translate-y-1 cursor-pointer ${selected ? "border-[#A78BFA]" : "border-white/10 hover:border-[#A78BFA]/40"}`}
      style={{ animationDelay: `${index * 40}ms`, opacity: 0 }}
    >
      <div className="relative aspect-[3/4] bg-black/40 overflow-hidden">
        {primaryImage ? (
          <img
            data-testid={`card-tile-web-image-${card.id}`}
            src={pcImageUrl(primaryImage, "240")}
            onError={primaryImage !== scannedImage ? handleWebImageError : undefined}
            alt={card.name}
            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${saleGray}`}
          />
        ) : (
          <div data-testid={`card-tile-no-image-${card.id}`} className="absolute inset-0 flex items-center justify-center text-zinc-700 text-xs">No photo</div>
        )}
        {showHover && (
          <>
            <img
              data-testid={`card-tile-scanned-image-${card.id}`}
              src={pcImageUrl(hoverImage, "240")}
              onError={hoverImage !== scannedImage ? handleWebImageError : undefined}
              alt={`${card.name} - alternate view`}
              className={`absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 ${saleGray}`}
            />
            <span className={`absolute ${actionBottom} right-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/70 text-zinc-300 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 pointer-events-none`}>
              {hoverLabel}
            </span>
          </>
        )}
        {saleStatus && (
          <>
            <div
              data-testid={`card-tile-sale-overlay-${card.id}`}
              className={`absolute inset-0 pointer-events-none ${saleStatus === "under_offer" ? "bg-[#7C3AED]/35" : "bg-red-600/35"}`}
            />
            <div
              data-testid={`card-tile-sale-badge-${card.id}`}
              className={`absolute bottom-0 inset-x-0 py-1 text-center pointer-events-none ${saleStatus === "under_offer" ? "bg-[#7C3AED]/85 text-white" : "bg-red-600/85 text-white"}`}
            >
              <span className="block text-[10px] uppercase tracking-[0.18em] font-bold leading-tight">
                {saleStatus === "under_offer" ? "Under Offer" : "Sold STC"}
              </span>
              {salePrice != null && (
                <span data-testid={`card-tile-sale-price-${card.id}`} className="block text-[11px] font-mono-plex font-semibold leading-tight">
                  {fmt(salePrice)}
                </span>
              )}
            </div>
          </>
        )}
        {card.status === "pending_review" && !viewOnly && (
          <span data-testid={`card-tile-pending-badge-${card.id}`} className="absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#F59E0B]/90 text-black font-semibold">
            Review
          </span>
        )}
        <span className="absolute top-2 right-2 flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-black/70 border border-white/10 text-zinc-300">
          <Layers3 className="w-3 h-3" /> {card.game_type}
        </span>
        {card.match_verified && (
          <span data-testid={`card-tile-verified-badge-${card.id}`} className="absolute top-9 right-2 flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300">
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        )}
        {selectable && !viewOnly && (
          <button
            data-testid={`card-tile-select-checkbox-${card.id}`}
            onClick={handleCheckboxClick}
            className={`absolute bottom-2 left-2 w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors ${selected ? "bg-[#A78BFA] border-[#A78BFA]" : "bg-black/60 border-white/40"}`}
          >
            {selected && <Check className="w-4 h-4 text-[#05060f]" />}
          </button>
        )}
        {!selectable && !viewOnly && (
          <button
            data-testid={`card-tile-delete-button-${card.id}`}
            onClick={handleDeleteClick}
            className={`absolute ${actionBottom} left-2 w-7 h-7 rounded-full flex items-center justify-center bg-black/70 border border-white/20 text-zinc-300 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 hover:bg-red-500/80 hover:text-white hover:border-red-500/50`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        {!selectable && !viewOnly && onRemoveMatch && card.reference_image_url && (
          <button
            data-testid={`card-tile-remove-match-button-${card.id}`}
            title="Remove web match — use my scan"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveMatch(card.id); }}
            className={`absolute ${actionBottom} left-10 w-7 h-7 rounded-full flex items-center justify-center bg-black/70 border border-white/20 text-zinc-300 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 hover:bg-[#F59E0B]/80 hover:text-black hover:border-[#F59E0B]/50`}
          >
            <ImageOff className="w-3.5 h-3.5" />
          </button>
        )}
        {!selectable && !viewOnly && onSaleStatus && (
          <button
            data-testid={`card-tile-sale-status-button-${card.id}`}
            title={saleStatus === "under_offer" ? "Under Offer — click to mark Sold STC" : saleStatus === "sold_stc" ? "Sold STC — click to mark available again" : "Mark as Under Offer"}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSaleStatus(card.id); }}
            className={`absolute ${actionBottom} ${onRemoveMatch && card.reference_image_url ? "left-[4.5rem]" : "left-10"} w-7 h-7 rounded-full flex items-center justify-center border transition-all duration-300 ${saleStatus ? `opacity-100 ${saleStatus === "under_offer" ? "bg-[#7C3AED]/90 border-[#7C3AED] text-white" : "bg-red-600/90 border-red-500 text-white"}` : "bg-black/70 border-white/20 text-zinc-300 opacity-0 group-hover:opacity-100 group-active:opacity-100 hover:bg-[#7C3AED]/80 hover:text-white hover:border-[#7C3AED]/50"}`}
          >
            <Tag className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-1">
        <h3 data-testid={`card-tile-name-${card.id}`} className="font-display font-semibold text-white text-sm leading-tight line-clamp-2">
          {card.name}
          {card.card_number && (
            <span data-testid={`card-tile-number-${card.id}`} className="text-zinc-500 font-normal">
              {" "}{String(card.card_number).includes("/") ? card.card_number : `#${card.card_number}`}
            </span>
          )}
        </h3>
        <p className="text-xs text-zinc-500 truncate">{card.set_name || "Unknown set"}</p>
        {card.serial_number && (
          <span data-testid={`card-tile-serial-${card.id}`} className="inline-flex items-center gap-1 self-start text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">
            <Hash className="w-2.5 h-2.5" /> {card.serial_number}
          </span>
        )}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span data-testid={`card-tile-price-${card.id}`} title={priceTitle} className={`font-mono-plex font-semibold min-w-0 truncate ${totalValue != null ? (isEstimate ? "text-[#F59E0B]" : "text-[#A78BFA]") : "text-zinc-600 text-xs"}`}>
            {totalValue != null ? `${isEstimate ? "~" : ""}${fmt(totalValue)}` : "No price data"}
          </span>
          {showTrend && (
            <span
              data-testid={`card-tile-trend-${card.id}`}
              title={`Recent sold prices trending ${trend > 0 ? "up" : "down"} ${Math.abs(trend)}%`}
              className={`flex items-center gap-0.5 text-[10px] font-semibold shrink-0 ${trend > 0 ? "text-emerald-400" : "text-red-400"}`}
            >
              {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {Math.abs(trend) >= 10 ? Math.round(Math.abs(trend)) : Math.abs(trend)}%
            </span>
          )}
          <span className="text-[10px] text-zinc-500 flex items-center gap-1.5 shrink-0">
            {(card.quantity || 1) > 1 && (
              <span data-testid={`card-tile-quantity-${card.id}`} className="px-1.5 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA] font-semibold">×{card.quantity}</span>
            )}
            {card.is_graded ? (
              <span data-testid={`card-tile-grade-${card.id}`} className="px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 font-semibold uppercase">
                {[card.grading_company, card.grade].filter(Boolean).join(" ") || "Graded"}
              </span>
            ) : (
              card.condition
            )}
          </span>
        </div>
        {(addedStr || confidenceDot) && (
          <div className="flex items-center justify-between text-[10px] text-zinc-600 mt-1.5 pt-1.5 border-t border-white/5">
            {addedStr ? <span data-testid={`card-tile-added-${card.id}`}>Added {addedStr}</span> : <span />}
            {confidenceDot && (
              <span
                data-testid={`card-tile-confidence-${card.id}`}
                title={`AI identification confidence: ${card.id_confidence}`}
                className="flex items-center gap-1 capitalize"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${confidenceDot}`} /> {card.id_confidence}
              </span>
            )}
          </div>
        )}
      </div>
    </Wrapper>
  );
}
